import React, { useEffect } from "react";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { ReactComponent as IconX } from "svg/x.svg";

interface CancelSubscriptionModalProps {
  onClose: () => void;
  onConfirm: () => void;
  onOpenContactForm: () => void;
  isLoading: boolean;
  renewDate: string | null;
}

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  onClose,
  onConfirm,
  onOpenContactForm,
  isLoading,
  renewDate,
}) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "inherit";
    };
  }, []);

  return (
    <div>
      {/* Overlay */}
      <div className="fixed z-modal-overlay inset-0 bg-purple-500 bg-opacity-75" onClick={onClose} />

      {/* Modal */}
      <div className="fixed z-modal-content top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[670px] h-auto rounded-[3px] bg-purple-200 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4.5 bg-purple-200">
          <HeaderBullet>Confirm Cancellation</HeaderBullet>
          <IconX className="w-[14px] cursor-pointer [&_path]:fill-white" onClick={onClose} />
        </div>

        {/* Content */}
        <div className="p-4.5 bg-purple-400">
          <p className="text-white text-[20px] font-semibold mb-4.5">We're sorry to see you go!</p>

          {/* Info Box */}
          <div className="rounded-[3px] p-4.5 mb-4.5 bg-[linear-gradient(227deg,#313160_-134.31%,#191937_100%)]">
            <p className="text-white text-sm font-semibold mb-4.5">Please note:</p>
            <ul className="space-y-[10px]  text-lavender-50 text-xs">
              <li className="flex items-start gap-2">
                <span className="text-accent-yellow-400">•</span>
                <span>You will not receive a refund for your remaining purchased subscription time.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent-yellow-400">•</span>
                <span>
                  Your <span className="text-accent-yellow-400 font-[500]">U.GG PLUS</span> benefits will continue until{" "}
                  <span className="text-white font-bold">{renewDate || "your billing period ends"}</span>
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-accent-yellow-400">•</span>
                <span>
                  If you are having issues cancelling, please email us at{" "}
                  <a href="mailto:support@u.gg" className="text-white font-bold">
                    support@u.gg
                  </a>{" "}
                  or contact us{" "}
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onOpenContactForm();
                    }}
                    className="text-white font-bold hover:underline"
                  >
                    here
                  </button>
                  .
                </span>
              </li>
            </ul>
          </div>

          {/* Buttons */}
          <div className="flex justify-center gap-4.5">
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 h-9 bg-accent-blue-400 hover:bg-accent-blue-400/80 px-2.5 px-3 text-white text-[13px] font-semibold rounded-[3px] transition-colors disabled:opacity-50"
            >
              {isLoading ? "Cancelling..." : "Confirm Cancellation"}
            </button>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 h-9 bg-lavender-500 hover:bg-lavender-500/80 px-2.5 px-3 text-white text-[13px] font-semibold rounded-[3px] transition-colors disabled:opacity-50"
            >
              Go back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancelSubscriptionModal;
